import React, { useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { usePublicCmsPage } from "@/hooks/useCms";
import { defaultTitle } from "@/libs/constant";

// Links added in the editor open in a new tab; make sure they cannot reach back to this page.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

/**
 * Language comes only from ?language_code= (the mobile API adds it to the web-view URL from the
 * user's access token). Without it the API serves the default language (English); the browser's
 * language is deliberately not used.
 */
const resolveLanguageCode = (queryValue) => {
  const code = (queryValue || "").trim().toLowerCase();
  return code || undefined;
};

/**
 * Public CMS page (no login, no admin layout): /cms/:slug
 * Shows the content saved in Admin > CMS Master. The mobile app opens this URL in a web view.
 * @returns {JSX.Element}
 */
const CmsPublicPage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const languageCode = resolveLanguageCode(searchParams.get("language_code"));

  const { data: response, isLoading, isError, error } = usePublicCmsPage(slug, languageCode);
  const page = response?.data;

  useEffect(() => {
    document.title = page?.title ? `${page.title} - WeAll` : defaultTitle;
  }, [page?.title]);

  // Admin-authored HTML is sanitised before it is rendered
  const safeHtml = useMemo(
    () =>
      DOMPurify.sanitize(page?.description || "", {
        USE_PROFILES: { html: true },
        ADD_ATTR: ["target"],
      }),
    [page?.description]
  );

  if (isLoading) {
    return (
      <div className="cms-public cms-public--center">
        <div className="spinner-border" role="status" aria-label="Loading" />
      </div>
    );
  }

  if (isError) {
    const isNotFound = error?.response?.status === 404;
    return (
      <div className="cms-public cms-public--center">
        <div className="cms-public__message">
          <h1>{isNotFound ? "Page not found" : "Something went wrong"}</h1>
          <p>
            {isNotFound
              ? "The page you are looking for is not available."
              : "We could not load this page. Please try again later."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="cms-public">
      <main className="cms-public__container">
        <h1 className="cms-public__title">{page?.title}</h1>
        <article className="cms-content" dangerouslySetInnerHTML={{ __html: safeHtml }} />
        {page?.last_updated_at && (
          <p className="cms-public__updated">
            {page.version ? `Version ${page.version} · ` : ""}Last updated: {page.last_updated_at}
          </p>
        )}
      </main>
    </div>
  );
};

export default CmsPublicPage;
